import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";

const OPTIONS = [
  { value: "present", label: "Present" },
  { value: "absent", label: "Absent" },
  { value: "excused", label: "Excused" },
];

// Classroom Roster -> per-student attendance editor. A simple 3-state
// picker plus an optional note, with an explicit "Clear (Not Recorded)"
// action — attendance is never assumed, only ever explicitly set.
export const AttendancePopover = ({ status, note, canEdit, onSave }) => {
  const [open, setOpen] = useState(false);
  const [draftStatus, setDraftStatus] = useState(status || "");
  const [draftNote, setDraftNote] = useState(note || "");
  const [saving, setSaving] = useState(false);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) {
      setDraftStatus(status || "");
      setDraftNote(note || "");
    }
  };

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      await onSave(nextStatus, draftNote);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return <StatusBadge status={status || "not_recorded"} />;
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[hsl(var(--role-accent))] rounded-full" data-testid="attendance-popover-trigger">
          <StatusBadge status={status || "not_recorded"} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-3" data-testid="attendance-popover-content">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Attendance</Label>
          <RadioGroup value={draftStatus} onValueChange={setDraftStatus} className="gap-2">
            {OPTIONS.map((o) => (
              <div key={o.value} className="flex items-center gap-2">
                <RadioGroupItem value={o.value} id={`attendance-${o.value}`} data-testid={`attendance-option-${o.value}`} />
                <Label htmlFor={`attendance-${o.value}`} className="text-sm font-normal cursor-pointer">{o.label}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Note (optional)</Label>
          <Textarea
            value={draftNote}
            onChange={(e) => setDraftNote(e.target.value)}
            placeholder="e.g. Called ahead, running 10 min late"
            className="min-h-[60px] text-sm"
            data-testid="attendance-note-input"
          />
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button variant="ghost" size="sm" disabled={saving} onClick={() => save(null)} data-testid="attendance-clear-button">
            Clear
          </Button>
          <Button size="sm" disabled={saving || !draftStatus} onClick={() => save(draftStatus)} data-testid="attendance-save-button">
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
